/* ============================================================
   Wallpaper calculator — trigger button, drawer, roll estimate.

   Everything is wired through delegated listeners on `document`,
   which exists the moment this file runs. That keeps the
   calculator working no matter when the script executes, how
   many triggers a template renders, or whether a section
   re-renders its markup in the theme editor.
   ============================================================ */
(function () {
  'use strict';

  // Rolls are 24" wide; 13'L = 156", 27'L = 324".
  const ROLL_WIDTH_IN = 24;
  const ROLL_LENGTH_IN = { '24W x 27L': 324, '24W x 13L': 156 };
  const DEFAULT_ROLL_LENGTH_IN = 156;

  const INIT_WALL_COUNT = 1;
  let wallCount = INIT_WALL_COUNT;

  // Scroll offset stashed while the body is locked behind the drawer.
  let lockedScrollY = 0;
  // The trigger that opened the drawer, so focus can return to it on close.
  let lastTrigger = null;

  function drawer() {
    return document.querySelector('.hc-calculator-drawer');
  }

  function isMetric() {
    const select = document.getElementById('measurement_select');
    return !!select && select.value === 'Metric';
  }

  function unitMajor() {
    return isMetric() ? 'm' : 'ft';
  }

  function unitMinor() {
    return isMetric() ? 'cm' : 'in';
  }

  function nth(d) {
    d -= 0;

    if (d > 3 && d < 21) return 'th';

    switch (d % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  /* ---------------- walls ---------------- */

  // Mirrors the static first-wall markup in hc-drawer-calculator.liquid so
  // added walls get the same numeric keypad on mobile as the original row.
  function wallRowMarkup(index) {
    const ordinal = index + nth(index);
    const numberAttrs = 'type="number" inputmode="decimal" step="any" min="0" value="" placeholder="0"';

    return (
      '<label class="control-label" for="length1Wall' + index + '">Length of ' + ordinal + ' wall</label>' +
      '<div class="form__group length-row">' +
        '<div class="form__item"><div class="form__item__input">' +
          '<input class="form-control input-sm length1" id="length1Wall' + index + '" ' + numberAttrs + '>' +
          '<div class="form__item__label uom-box">' + unitMajor() + '</div>' +
        '</div></div>' +
        '<div class="form__item"><div class="form__item__input">' +
          '<input class="form-control input-sm length2" id="length2Wall' + index + '" ' + numberAttrs +
            ' aria-label="Length of ' + ordinal + ' wall, additional ' + unitMinor() + '">' +
          '<div class="form__item__label uom-box">' + unitMinor() + '</div>' +
        '</div></div>' +
      '</div>'
    );
  }

  function addWall() {
    const additionalWallRows = document.getElementById('additionalWallRows');
    if (!additionalWallRows) return;

    wallCount++;
    additionalWallRows.insertAdjacentHTML('beforeend', wallRowMarkup(wallCount));
  }

  /* ---------------- units ---------------- */

  // Each .form__group holds exactly one major and one minor unit box, so
  // pair them per row rather than relying on a global index.
  function syncUnitLabels() {
    const root = drawer();
    if (!root) return;

    const major = unitMajor();
    const minor = unitMinor();

    root.querySelectorAll('.form__group').forEach(function (group) {
      const boxes = group.querySelectorAll('.uom-box');
      if (boxes[0]) boxes[0].textContent = major;
      if (boxes[1]) boxes[1].textContent = minor;
    });

    const measurementBlock = document.querySelector('.measurement-block');
    if (measurementBlock) measurementBlock.classList.toggle('imperial', !isMetric());
  }

  /* ---------------- reset ---------------- */

  function resetFields() {
    const root = drawer();
    if (!root) return;

    const additionalWallRows = document.getElementById('additionalWallRows');
    if (additionalWallRows) additionalWallRows.innerHTML = '';
    wallCount = INIT_WALL_COUNT;

    // Clears the height/length inputs and the result field in one pass, so a
    // missing or renamed field can no longer break the whole reset.
    root.querySelectorAll('input').forEach(function (input) {
      input.value = '';
    });

    const heightError = document.getElementById('heighterror');
    if (heightError) heightError.style.display = 'none';
  }

  /* ---------------- estimate ---------------- */

  function estimate() {
    const output = document.getElementById('wallPaperCalcOutput');
    const height1 = document.getElementById('height1');
    const height2 = document.getElementById('height2');
    if (!output || !height1 || !height2) return;

    const rollsSizeSelect = document.getElementById('hc-rolls-size');
    const rollsSize = rollsSizeSelect ? rollsSizeSelect.value : '';
    const rollLength = ROLL_LENGTH_IN[rollsSize] || DEFAULT_ROLL_LENGTH_IN;

    // ---- wall measurements, always normalised to inches ----
    const metric = isMetric();
    function toInches(major, minor) {
      major = parseFloat(major);
      minor = parseFloat(minor);
      if (isNaN(major)) major = 0;
      if (isNaN(minor)) minor = 0;
      // imperial: feet + inches | metric: metres + centimetres
      return metric ? ((major * 100) + minor) / 2.54 : (major * 12) + minor;
    }

    const wallHeight = toInches(height1.value, height2.value);

    // Wallpaper hangs in full-height vertical strips ("drops"). Round each wall
    // up to whole strips so every wall/corner starts a fresh drop.
    let totalDrops = 0;
    document.querySelectorAll('.hc-calculator-drawer .length-row').forEach(function (row) {
      const length1 = row.querySelector('.length1');
      const length2 = row.querySelector('.length2');
      const wallWidth = toInches(length1 ? length1.value : 0, length2 ? length2.value : 0);
      if (wallWidth > 0) {
        totalDrops += Math.ceil(wallWidth / ROLL_WIDTH_IN);
      }
    });

    // Nothing to estimate until both a height and at least one wall are entered.
    if (wallHeight <= 0 || totalDrops <= 0) {
      output.value = '';
      return;
    }

    // A roll only yields *whole* drops, so the real limit is drops-per-roll, not
    // raw area. Add a safe per-drop allowance: 4" trim + a 24" pattern repeat.
    // Painted Paper designs repeat every 12"-48"; most are 24", so baking in 24"
    // keeps the estimate right for the typical design without a repeat field.
    const TRIM_AND_REPEAT = 28;
    const dropLength = wallHeight + TRIM_AND_REPEAT;

    let totalSets;
    const dropsPerRoll = Math.floor(rollLength / dropLength);
    if (dropsPerRoll < 1) {
      // Wall taller than a single roll can span in one piece.
      totalSets = totalDrops * Math.ceil(dropLength / rollLength);
    } else {
      totalSets = Math.ceil(totalDrops / dropsPerRoll);
    }

    // ~10% cutting-error buffer, rounded down: the per-drop trim/repeat
    // allowance above already protects small jobs, so only larger orders
    // (10+ rolls) accrue extra rolls here.
    totalSets += Math.floor(totalSets * 0.1);

    output.value = totalSets;
  }

  /* ---------------- open / close ---------------- */

  function isOpen() {
    const root = drawer();
    return !!root && root.classList.contains('drawer--open');
  }

  function openDrawer(trigger) {
    const root = drawer();
    if (!root) return;

    lastTrigger = trigger || null;

    // iOS Safari ignores `overflow: hidden` on the body, so the background is
    // pinned with `position: fixed` instead and the offset restored on close.
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.body.style.top = -lockedScrollY + 'px';
    document.body.classList.add('hc-calculator-active');

    root.classList.add('drawer--open');
    root.setAttribute('aria-hidden', 'false');

    const overlay = document.querySelector('.hc-overlay');
    if (overlay) overlay.classList.add('hc-overlay-active');

    // Move focus into the drawer without summoning the mobile keyboard.
    try {
      root.focus({ preventScroll: true });
    } catch (e) {
      root.focus();
    }
  }

  function closeDrawer() {
    const root = drawer();
    if (!root) return;

    root.classList.remove('drawer--open');
    root.setAttribute('aria-hidden', 'true');

    const overlay = document.querySelector('.hc-overlay');
    if (overlay) overlay.classList.remove('hc-overlay-active');

    document.body.classList.remove('hc-calculator-active');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);

    const trigger = lastTrigger || document.querySelector('.hc-calculator-trigger');
    if (trigger) trigger.focus();
    lastTrigger = null;
  }

  // iOS Safari restores pages from its back/forward cache with the DOM intact.
  // Without this, hitting Back with the drawer open returns to a page whose
  // body is still pinned and unscrollable.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && isOpen()) closeDrawer();
  });

  /* ---------------- delegated events ---------------- */

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    // preventDefault also neutralises a trigger that renders without
    // type="button" and would otherwise submit a surrounding form.
    const trigger = target.closest('.hc-calculator-trigger');
    if (trigger) {
      event.preventDefault();
      openDrawer(trigger);
    } else if (target.closest('.hc-drawer-close') || target.closest('.hc-overlay')) {
      event.preventDefault();
      closeDrawer();
    } else if (target.closest('.hc-add-wall')) {
      event.preventDefault();
      addWall();
    } else if (target.closest('.hc-estimate')) {
      event.preventDefault();
      estimate();
    } else if (target.closest('.hc-reset')) {
      event.preventDefault();
      resetFields();
    }
  });

  document.addEventListener('change', function (event) {
    if (event.target && event.target.id === 'measurement_select') {
      resetFields();
      syncUnitLabels();
    }
  });

  document.addEventListener('keydown', function (event) {
    if ((event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) && isOpen()) {
      closeDrawer();
    }
  });

  // The unit <select> carries an inline onchange in older saved templates.
  window.wallPaperCalcUnitsChangeFunc = function () {
    resetFields();
    syncUnitLabels();
  };
})();
